'use client';

import { LoadingButton } from '@/components/common/LoadingButton';
import { type ProductSimple } from '@/domain/products/types';
import { addFilter, clearFilters, removeFilter, setFilter, useSolutionStore } from '@/features/products/store/solution';
import {
  type Filter,
  type FilterChild,
  FilterGroup,
  type FilterItem,
  type FilterList,
  type Filters,
  type Option,
  ShowType,
} from '@/features/products/types';
import pdf from '@/services/pdf';
import { capitalizeFirstLetter } from '@/utils/string';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Tooltip as MuiTooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Checkbox } from '@andritzot/metris-web-ui/inputs/checkbox';
import { Radio } from '@andritzot/metris-web-ui/inputs/radio';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Accordion, AccordionDetails, AccordionSummary } from '@andritzot/metris-web-ui/surfaces/accordion';
import { Popover } from '@andritzot/metris-web-ui/utils/popover';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListOutlined from '@mui/icons-material/FilterListOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import { useRouter, useSearchParams } from 'next/navigation';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import ShowTypeSelection from './ShowTypeSelection';

function getFilterGroupValues(filterList: FilterList, filterGroup: FilterGroup): FilterItem[] {
  switch (filterGroup) {
    case FilterGroup.BENEFIT:
      return filterList.benefits.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.BENEFIT_GROUP:
      return filterList.benefits.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.COUNTRY:
      return filterList.countries.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.INDUSTRY:
      return filterList.industries.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.INDUSTRY_AREA:
      return filterList.industries.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.PLANT_LIFECYCLE:
      return filterList.plantLifecycleManagement.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.PLANT_LIFECYCLE_GROUP:
      return filterList.plantLifecycleManagement.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.PLATFORM:
      return filterList.platform;
    case FilterGroup.PRODUCT_LINE:
      return filterList.groups.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.PRODUCT_GROUP:
      return filterList.groups.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.REGION:
      return filterList.countries.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.USER_PERSONA:
      return filterList.userPersonas;
    case FilterGroup.PORTFOLIO:
      return filterList.portfolios;
    case FilterGroup.PRODUCT_UNIT:
      return filterList.productUnit;
    default:
      return [];
  }
}
interface ProductFilterProps {
  filteredProducts: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isInternal: boolean;
}

const ProductFilter = ({ filteredProducts, filterList, filtersValue, isInternal }: ProductFilterProps) => {
  const showType = useSolutionStore((s) => s.showType);
  const industry = useSolutionStore((s) => s.industry);
  const searchParams = useSearchParams();
  const router = useRouter();
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [sharedQuery, setSharedQuery] = useState('');
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const filters = useSolutionStore((s) => s.filters);

  const filterExists = useCallback(
    (id: number, group: FilterGroup) => {
      return filters.some((f) => f.id === id && f.group === group);
    },
    [filters],
  );

  const toggleFilter = useCallback(
    (
      id: number,
      name: string,
      group: FilterGroup,
      parentId: number | null = null,
      parentGroup: FilterGroup | null = null,
    ) => {
      if (filterExists(id, group)) {
        removeFilter({ id, group });
        if (parentId && parentGroup) {
          removeFilter({ id: parentId, group: parentGroup });
        }
      } else {
        addFilter({ id, name, group });
      }
    },
    [filterExists],
  );

  const toggleParentFilter = useCallback(
    (node: FilterChild, group: FilterGroup) => {
      const currentFilters = useSolutionStore.getState().filters;

      if (filterExists(node.id, group)) {
        const childIds = new Set(node.values.map((v) => v.id));
        const newFilters = currentFilters.filter(
          (f) => !(f.id === node.id && f.group === group) && !(childIds.has(f.id) && f.group === node.group),
        );
        useSolutionStore.setState({ filters: newFilters });
      } else {
        const existingIds = new Set(currentFilters.filter((f) => f.group === node.group).map((f) => f.id));
        const newEntries: Filter[] = [
          { id: node.id, name: node.label, group },
          ...node.values
            .filter(({ id }) => !existingIds.has(id))
            .map(({ id, value }) => ({ id, name: value, group: node.group })),
        ];
        useSolutionStore.setState({ filters: [...currentFilters, ...newEntries] });
      }
    },
    [filterExists],
  );

  useEffect(() => {
    const queryFilters = searchParams.getAll('q');
    if (queryFilters.length && !filters.length && filterList) {
      const queryObject: Filter[] = [];
      queryFilters.forEach((filter) => {
        const [idString, groupString] = filter.split(';');
        const id = Number.parseInt(idString, 10);
        const group: FilterGroup = Number.parseInt(groupString, 10);

        const values = getFilterGroupValues(filterList, group);

        const item = values.find((value) => value.id === id);
        if (item) {
          queryObject.push({ id, group, name: item.value } satisfies Filter);
        }
      });
      if (queryObject.length) {
        router.push('/');
        useSolutionStore.setState({ filters: queryObject });
      }
    }
  }, [filterList, filters.length, router, searchParams]);

  const shareFilters = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      const query = filters.map((f) => `q=${f.id};${f.group}`).join('&');
      const url = `${globalThis.location.origin}?${query}`;
      if (url.length > 2000) {
        toast.error('Too many filters. Max size has been exceeded');
        return;
      }
      setSharedQuery(url);
      setPopoverAnchor(e.currentTarget);
    },
    [filters],
  );

  const downloadSaleableProducts = useCallback(async () => {
    setIsLoadingExport(true);
    const ids = filteredProducts.filter((p) => p.saleable).map(({ idProduct }) => idProduct);

    if (!ids) {
      toast.warning('No saleable products available for given portfolio');
      return;
    }
    try {
      const portf = Object.values(filters).find((f) => f.group === FilterGroup.PORTFOLIO);
      const title = portf?.name ?? 'Saleable Products';
      await pdf.downloadPDFAsync('/pdf/saleable-solutions', title, { title, ids });
      toast.success('PDF downloaded successfully!');
    } catch {
      toast.error('Error downloading PDF!');
    } finally {
      setIsLoadingExport(false);
    }
  }, [filteredProducts, filters]);

  const renderCheckboxes = (
    { values, group }: Filters,
    parentGroup: FilterGroup | null = null,
    parentId: number | null = null,
  ) =>
    values.map(({ id, value, tooltip }) => {
      const tooltipId = `${id}_${value.split(' ')[0]}`;
      return (
        value && (
          <MuiTooltip key={value} title={tooltip ?? ''} placement="right">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '0.25rem' }} id={tooltipId}>
              <Checkbox
                size="small"
                id={value}
                checked={filterExists(id, group)}
                onChange={() => toggleFilter(id, value, group, parentId, parentGroup)}
                sx={{ py: 0 }}
              />
              <label htmlFor={value} style={{ lineHeight: 1.25, cursor: 'pointer' }}>
                {capitalizeFirstLetter(value)}
              </label>
            </Box>
          </MuiTooltip>
        )
      );
    });

  const renderCheckboxGroup = ({ group, children }: Filters) => (
    <Box>
      {children?.map((node, index) => {
        const hasChildren = !!node.values.length;
        return (
          <MuiTooltip key={node.label} title={node.label} placement="right">
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: '0.25rem' }} id={`g${group}-p-${index}`}>
                <Checkbox
                  size="small"
                  id={node.label}
                  checked={filterExists(node.id, group)}
                  onChange={() => {
                    if (hasChildren) {
                      toggleParentFilter(node, group);
                    } else {
                      toggleFilter(node.id, node.label, node.group);
                    }
                  }}
                  sx={{ py: 0 }}
                />
                <label htmlFor={node.label} style={{ cursor: 'pointer' }}>
                  {node.label}
                </label>
              </Box>
              <Box sx={{ ml: '20px' }}>{renderCheckboxes(node, group, node.id)}</Box>
            </Box>
          </MuiTooltip>
        );
      })}
    </Box>
  );

  const renderRadioButtonGroup = ({ group, values }: Filters) => (
    <Box>
      {values.map(({ id, value }) => (
        <Box key={value} sx={{ display: 'flex', alignItems: 'center', mb: '0.25rem' }}>
          <Radio
            size="small"
            value={value}
            onChange={() => setFilter({ id, name: value, group })}
            checked={filterExists(id, group)}
            sx={{ py: 0 }}
          />
          <label style={{ cursor: 'pointer' }}>{value}</label>
        </Box>
      ))}
    </Box>
  );

  const renderOptions = (options?: Option[]) =>
    options?.length ? (
      <Box
        sx={{
          p: '5px',
          display: { xs: 'block', md: 'flex' },
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {options.map((option) => (
          <Box key={option.value} sx={{ display: 'flex', alignItems: 'center', mr: '10px', mb: '0.25rem' }}>
            <Radio
              size="small"
              value={option.value}
              onChange={() => useSolutionStore.setState({ industry: option.value })}
              checked={industry === option.value}
              sx={{ py: 0 }}
            />
            <label htmlFor={option.value} style={{ cursor: 'pointer' }}>
              {capitalizeFirstLetter(option.value)}
            </label>
          </Box>
        ))}
      </Box>
    ) : null;

  const handleClickCopy = useCallback(async () => {
    await navigator.clipboard.writeText(sharedQuery);
    textAreaRef.current?.focus();
    textAreaRef.current?.select();
    toast.success('Copied to clipboard');
  }, [sharedQuery]);

  const renderFilterTitle = useMemo(
    () => (
      <>
        <Box
          sx={{
            display: { xs: 'grid', lg: 'flex' },
            flexFlow: { lg: 'column' },
            alignItems: 'center',
            mr: { xs: '1rem', lg: 0 },
            '& p': { flex: 1, m: 0 },
            '& button': { alignSelf: 'flex-start' },
          }}
        >
          {filters.length ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'center', lg: 'flex-start' },
                flexDirection: { xs: 'column', lg: 'row' },
                mr: '1rem',
                width: '100%',
                '& button': { width: '100%', whiteSpace: 'nowrap', py: '0.5rem' },
              }}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseOutlined />}
                onClick={() => clearFilters()}
                sx={{ mr: '0.2rem' }}
              >
                Clear filters
              </Button>
              <Button variant="outlined" startIcon={<SendOutlined />} onClick={shareFilters}>
                Share filters
              </Button>
            </Box>
          ) : null}
          <Box sx={{ flex: { lg: 1 } }}>
            <Typography>
              Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'solution' : 'solutions'}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <FilterListOutlined fontSize="small" />
            <Typography variant="body2">
              <b>Filters</b>
            </Typography>
          </Box>
        </Divider>
        <Popover
          open={!!popoverAnchor}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, width: '450px' } } }}
        >
          <TextField
            multiline
            rows={3}
            fullWidth
            value={sharedQuery}
            inputRef={textAreaRef}
            sx={{ mb: 1 }}
            slotProps={{ input: { readOnly: true } }}
          />
          <Button variant="text" startIcon={<ContentCopyOutlined />} onClick={handleClickCopy}>
            Copy URL
          </Button>
        </Popover>
      </>
    ),
    [filteredProducts.length, filters.length, handleClickCopy, popoverAnchor, shareFilters, sharedQuery],
  );

  const renderChildren = (filterValue: Filters) =>
    filterValue.children ? renderCheckboxGroup(filterValue) : renderCheckboxes(filterValue);

  const onChangeShowType = (value: ShowType[]) => {
    useSolutionStore.setState({ showType: value.length ? value : ['Show Solutions'] });
  };

  return (
    <Box sx={{ height: 'calc(100vh - 110px)', px: '0.5rem', pb: '20px', overflowY: 'auto' }}>
      {renderFilterTitle}
      {isInternal && <ShowTypeSelection onChange={onChangeShowType} showType={showType} />}
      {filtersValue
        .filter((f) => f.values.length || f.children?.length)
        .map((filterValue) => (
          <Accordion key={filterValue.label} disableGutters sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ '& a': { color: 'text.primary', textDecoration: 'none' }, '& h5': { m: 0 } }}
            >
              <Typography variant="h6" component="h5">
                {filterValue.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderOptions(filterValue.options)}
              {filterValue.singleSelect ? renderRadioButtonGroup(filterValue) : renderChildren(filterValue)}
              {filterValue.group === FilterGroup.PORTFOLIO && (
                <MuiTooltip
                  title={
                    Object.values(filters).some((f) => f.group === FilterGroup.PORTFOLIO)
                      ? 'Export Saleable Products to PDF'
                      : 'Select at least one portfolio to enable export'
                  }
                >
                  <Typography component="span">
                    <LoadingButton
                      loading={isLoadingExport}
                      icon={<PictureAsPdfOutlined />}
                      variant="outlined"
                      sx={{ mt: 2 }}
                      disabled={!Object.values(filters).some((f) => f.group === FilterGroup.PORTFOLIO)}
                      onClick={downloadSaleableProducts}
                      type="button"
                    >
                      Export Saleable Products
                    </LoadingButton>
                  </Typography>
                </MuiTooltip>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  );
};

export default ProductFilter;
